import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useService } from "../../Context/ServiceContext";
import { getServiceCategories } from "../../services/api/serviceApi";
import { extractErrorMessage } from "../../services/api/errorHandler";
import ServiceTwoView from "./components/ServiceTwoView";

const SERVICE_DEFINITIONS = [
  {
    key: "engine-oil",
    title: "روغن موتور",
    icon: "oil",
    description: "روغن موتور و فیلترهای مصرفی",
    aliases: ["روغن موتور"],
    options: [
      { key: "oil", title: "روغن موتور", aliases: ["روغن موتور"] },
      { key: "air-filter", title: "فیلتر هوا", aliases: ["فیلتر هوا"] },
      { key: "cabin-filter", title: "فیلتر کابین", aliases: ["فیلتر کابین"] },
      { key: "oil-filter", title: "فیلتر روغن", aliases: ["فیلتر روغن"] },
      {
        key: "fuel-filter",
        title: "صافی بنزین",
        aliases: ["صافی بنزین", "فیلتر بنزین"],
      },
    ],
  },
  {
    key: "timing-belt",
    title: "تسمه تایم",
    icon: "belt",
    description: "بازدید یا تعویض تسمه تایم",
    aliases: ["تسمه تایم"],
  },
  {
    key: "gearbox-oil",
    title: "روغن گیربکس",
    icon: "gearbox",
    description: "سرویس روغن گیربکس",
    aliases: ["روغن گیربکس", "گیربکس"],
  },
  {
    key: "alternator-belt",
    title: "تسمه دینام",
    icon: "belt",
    description: "بازدید یا تعویض تسمه دینام",
    aliases: ["تسمه دینام", "تسمه"],
  },
  {
    key: "spark-plug",
    title: "شمع",
    icon: "spark",
    description: "بازدید و تعویض شمع موتور",
    aliases: ["شمع"],
  },
  {
    key: "antifreeze",
    title: "ضدیخ",
    icon: "fluid",
    description: "تعویض و کنترل مایع خنک‌کننده",
    aliases: ["ضدیخ", "خنک"],
  },
  {
    key: "brake-pad",
    title: "لنت ترمز",
    icon: "brake",
    description: "لنت‌های محور جلو و عقب",
    aliases: ["لنت", "پد ترمز", "ترمز"],
    options: [
      {
        key: "front",
        title: "لنت ترمز جلو",
        aliases: ["لنت جلو", "پد ترمز", "لنت", "ترمز"],
      },
      {
        key: "rear",
        title: "لنت ترمز عقب",
        aliases: ["لنت عقب", "پد ترمز", "لنت", "ترمز"],
      },
    ],
  },
  {
    key: "hydraulic-oil",
    title: "روغن هیدرولیک",
    icon: "fluid",
    description: "روغن فرمان هیدرولیک",
    aliases: ["روغن هیدرولیک", "هیدرولیک"],
  },
  {
    key: "brake-fluid",
    title: "روغن ترمز",
    icon: "fluid",
    description: "کنترل یا تعویض روغن ترمز",
    aliases: ["روغن ترمز", "ترمز"],
  },
  {
    key: "clutch",
    title: "دیسک و صفحه کلاچ",
    icon: "clutch",
    description: "سرویس مجموعه کلاچ",
    aliases: ["دیسک و صفحه", "کلاچ"],
  },
  {
    key: "tires",
    title: "لاستیک",
    icon: "tire",
    description: "انتخاب سریع موقعیت هر چرخ",
    aliases: ["لاستیک", "تایر"],
    kind: "tires",
  },
  {
    key: "inspection",
    title: "معاینه فنی",
    icon: "inspection",
    description: "ثبت نتیجه معاینه فنی خودرو",
    aliases: ["معاینه فنی", "معاینه"],
  },
  {
    key: "insurance",
    title: "بیمه",
    icon: "insurance",
    description: "ثبت یا تمدید بیمه خودرو",
    aliases: ["بیمه"],
  },
  {
    key: "battery",
    title: "باتری",
    icon: "battery",
    description: "تست یا تعویض باتری",
    aliases: ["باتری"],
  },
  {
    key: "gearbox-repair",
    title: "تعمیر گیربکس",
    icon: "gearbox",
    description: "بازدید و تعمیر مجموعه گیربکس",
    aliases: ["تعمیر گیربکس", "تعمیر موتور و گیربکس", "گیربکس"],
  },
  {
    key: "car-wash",
    title: "کارواش",
    icon: "wash",
    description: "شست‌وشوی خودرو",
    aliases: ["کارواش", "شست"],
  },
  {
    key: "window-tint",
    title: "شیشه دودی",
    icon: "window",
    description: "نصب یا ترمیم شیشه دودی",
    aliases: ["شیشه دودی", "شیشه"],
  },
];

const TIRE_POSITIONS = [
  { key: "front-right", title: "لاستیک جلو راست" },
  { key: "front-left", title: "لاستیک جلو چپ" },
  { key: "rear-right", title: "لاستیک عقب راست" },
  { key: "rear-left", title: "لاستیک عقب چپ" },
];

const includesAny = (title, aliases = []) =>
  aliases.some((alias) => String(title || "").includes(alias));

function buildCatalog(categories) {
  const fallback = categories[0];
  const findBackendCategory = (aliases, parentAliases = []) =>
    categories.find((item) => includesAny(item.title, aliases)) ||
    categories.find((item) => includesAny(item.title, parentAliases)) ||
    fallback;
  return SERVICE_DEFINITIONS.map((definition) => {
    const rawOptions =
      definition.kind === "tires"
        ? TIRE_POSITIONS
        : definition.options || [
            {
              key: "main",
              title: definition.title,
              aliases: definition.aliases,
            },
          ];
    const options = rawOptions.map((option) => {
      const backendCategory = findBackendCategory(
        option.aliases || definition.aliases,
        definition.aliases,
      );
      return {
        ...option,
        id: `${definition.key}:${option.key}`,
        backendCategoryId: backendCategory?.id,
        defaultIntervalKm: backendCategory?.defaultIntervalKm,
        defaultIntervalMonths: backendCategory?.defaultIntervalMonths,
      };
    });
    return { ...definition, options };
  });
}

export default function ServiceTwo() {
  const navigate = useNavigate();
  const location = useLocation();
  const isCustomerSelection = location.pathname === "/SelectService";
  const shouldOpenDetails = new URLSearchParams(location.search).get("view") === "details";
  const {
    selectedCategories,
    categoryDetails,
    toggleCategoryOption,
    setCategoryDetail,
    setPendingServices,
    clearServiceSelections,
  } = useService();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedServiceKeys, setSelectedServiceKeys] = useState(() => [
    ...new Set(
      Object.keys(selectedCategories)
        .filter((id) => selectedCategories[id])
        .map((id) => id.split(":")[0]),
    ),
  ]);
  const [showDetails, setShowDetails] = useState(shouldOpenDetails);
  const [activeCategory, setActiveCategory] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    getServiceCategories()
      .then((response) => {
        if (cancelled) return;
        if (!response?.success || !Array.isArray(response.data))
          throw new Error(
            response?.message || "دریافت فهرست سرویس‌ها ممکن نشد",
          );
        setCategories(response.data);
      })
      .catch((error) => {
        if (!cancelled)
          setLoadError(
            extractErrorMessage(error, "دریافت فهرست سرویس‌ها ممکن نشد"),
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [showDetails]);

  const catalog = useMemo(() => buildCatalog(categories), [categories]);
  const selectedServices = useMemo(
    () =>
      catalog.filter((service) => selectedServiceKeys.includes(service.key)),
    [catalog, selectedServiceKeys],
  );
  const serviceItems = useMemo(
    () => selectedServices.flatMap((service) => service.options),
    [selectedServices],
  );
  const serviceItemIds = useMemo(
    () => new Set(serviceItems.map((item) => item.id)),
    [serviceItems],
  );
  const selectedIds = useMemo(
    () =>
      Object.keys(selectedCategories).filter(
        (id) => Boolean(selectedCategories[id]) && serviceItemIds.has(id),
      ),
    [selectedCategories, serviceItemIds],
  );

  const toggleService = (serviceKey) => {
    setSelectedServiceKeys((current) =>
      current.includes(serviceKey)
        ? current.filter((key) => key !== serviceKey)
        : [...current, serviceKey],
    );
    setSubmitError("");
  };

  const setAction = (category, action) => {
    toggleCategoryOption(category.id, action);
    setSubmitError("");
  };

  const openDetails = (category) => {
    if (!selectedCategories[category.id])
      toggleCategoryOption(category.id, "L");
    setActiveCategory(category);
  };

  const removeCategory = (category) => {
    const selectedAction = selectedCategories[category.id];
    if (selectedAction) toggleCategoryOption(category.id, selectedAction);
    if (activeCategory?.id === category.id) setActiveCategory(null);
  };

  const saveCategoryDetails = (details) => {
    if (!activeCategory) return;
    setCategoryDetail(activeCategory.id, details);
    setActiveCategory(null);
  };

  const setTireAction = (service, action) => {
    const selectedTires = service.options.filter(
      (item) => selectedCategories[item.id],
    );
    selectedTires.forEach((item) => {
      if (selectedCategories[item.id] !== action)
        toggleCategoryOption(item.id, action);
    });
  };

  const handleServiceConfirmation = () => {
    if (!selectedIds.length)
      return setSubmitError("حداقل یک سرویس را انتخاب کنید.");
    setSubmitError("");
    const selectedItems = selectedIds
      .map((id) => serviceItems.find((item) => item.id === id))
      .filter(Boolean)
      .map((category) => ({
        ...category,
        action: selectedCategories[category.id],
        ...(categoryDetails[category.id] || {}),
      }));
    setPendingServices(selectedItems);
    clearServiceSelections();

    if (isCustomerSelection) {
      const firstCategoryId = selectedItems[0]?.backendCategoryId;
      if (firstCategoryId) {
        navigate(`/Periodic/${firstCategoryId}`, {
          state: {
            ...(location.state || {}),
            serviceSelectionCompleted: true,
            customerServiceRegistration: true,
            pendingServices: selectedItems,
          },
        });
      } else {
        navigate("/", {
          state: { ...(location.state || {}), serviceSelectionCompleted: true },
        });
      }
      return;
    }

    navigate("/ServiceOne", {
      state: { ...(location.state || {}), serviceSelectionCompleted: true },
    });
  };

  return (
    <ServiceTwoView
      backRoute={location.pathname === "/SelectService" ? "/" : "/PlateCapture"}
      showDetails={showDetails}
      loading={loading}
      loadError={loadError}
      catalog={catalog}
      selectedServiceKeys={selectedServiceKeys}
      selectedServices={selectedServices}
      selectedCategories={selectedCategories}
      categoryDetails={categoryDetails}
      selectedIds={selectedIds}
      activeCategory={activeCategory}
      submitError={submitError}
      onToggleService={toggleService}
      onRetry={() => setReloadKey((value) => value + 1)}
      onBackToCatalog={() => setShowDetails(false)}
      onAction={setAction}
      onOpenDetails={openDetails}
      onToggleTire={(item) =>
        setAction(item, selectedCategories[item.id] || "L")
      }
      onTireAction={setTireAction}
      onContinueToDetails={() => {
        setSubmitError("");
        setShowDetails(true);
      }}
      onConfirm={handleServiceConfirmation}
      onSaveDetails={saveCategoryDetails}
      onRemoveDetails={(category) => removeCategory(category)}
      onCloseDetails={() => setActiveCategory(null)}
    />
  );
}
